import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, TextInput } from '@/components/ui/daisy';
import Unit from '@/models/Unit';

export type UnitFormFields = {
  name: string;
};

type UnitFormProps = {
  unit?: Unit;
  isLoading?: boolean;
  onSubmit: (data: UnitFormFields) => void;
};

export default function UnitForm({ unit, isLoading, onSubmit }: UnitFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.unitForm.form',
  });

  const [hasChanges, setHasChanges] = useState(false);

  const defaultValues = useMemo(
    () => ({
      name: unit?.name ?? '',
    }),
    [unit],
  );

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UnitFormFields>({
    defaultValues,
  });

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      setHasChanges(false);
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <TextInput.Fieldset
        label={t('unitName')}
        labelAlt={errors?.name?.message}
      >
        <TextInput
          type="text"
          placeholder={t('unitNamePlaceholder')}
          className="w-full"
          color={!errors?.name?.message ? 'default' : 'error'}
          {...register('name', { required: t('unitNameRequired') })}
        />
      </TextInput.Fieldset>
      <div className="flex justify-end">
        <Button
          type="submit"
          color="primary"
          className="px-16"
          disabled={isLoading || !hasChanges}
          loading={isLoading}
        >
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
